import { useMemo, useState } from 'react'
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Loader2,
  MessageSquare,
} from 'lucide-react'
import type { StepEventDTO, StepEventPayload } from '@deepguide-ai/types'
import { useStepEventStream } from '@/hooks/useStepEventStream'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'

type StepEvent = StepEventDTO

interface StepRunTimelineProps {
  stepRunId: string
  executionId: string
  /** Stop tailing the SSE stream — set false once the step finalized. */
  enabled?: boolean
  /** Optional empty-state message (e.g. "Waiting for first tool call…"). */
  emptyLabel?: string
}

interface TimelineEntry {
  toolCallId: string
  toolName: string
  call: StepEvent | null
  result: StepEvent | null
  isError: boolean
  startedAt: Date | string | null
}

/**
 * Live, ordered timeline of tool-call events emitted by a runbook step.
 * Pairs `tool-call` (input-available) → `tool-result`/`tool-error` events
 * by `toolCallId` so each row collapses into one card with status + I/O.
 */
export function StepRunTimeline({
  stepRunId,
  executionId,
  enabled = true,
  emptyLabel = 'Waiting for first tool call…',
}: StepRunTimelineProps) {
  const { events, isLoading } = useStepEventStream({
    stepRunId,
    executionId,
    enabled,
  })

  const entries = useMemo(() => buildEntries(events), [events])

  if (isLoading && entries.length === 0) {
    return (
      <div className='py-3 text-center text-[11px] text-text-placeholder'>
        Loading timeline…
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className='py-3 text-center text-[11px] text-text-placeholder'>
        {emptyLabel}
      </div>
    )
  }

  return (
    <ul className='divide-y divide-border'>
      {entries.map((entry) =>
        entry.toolCallId.startsWith('log:') ? (
          <LogRow key={entry.toolCallId} entry={entry} />
        ) : (
          <ToolCallRow key={entry.toolCallId} entry={entry} />
        ),
      )}
    </ul>
  )
}

function buildEntries(events: StepEvent[]): TimelineEntry[] {
  const byCallId = new Map<string, TimelineEntry>()
  const order: string[] = []

  for (const evt of events) {
    const payload = evt.payload
    if (payload.kind === 'log') {
      const id = `log:${evt.seq}`
      order.push(id)
      byCallId.set(id, {
        toolCallId: id,
        toolName: 'log',
        call: evt,
        result: null,
        isError: false,
        startedAt: evt.createdAt,
      })
      continue
    }

    const callId = payload.toolCallId ?? `seq-${evt.seq}`
    const existing = byCallId.get(callId)

    if (payload.kind === 'tool-call') {
      if (existing) {
        existing.call = evt
        existing.toolName = payload.toolName ?? existing.toolName
        existing.startedAt = existing.startedAt ?? evt.createdAt
      } else {
        order.push(callId)
        byCallId.set(callId, {
          toolCallId: callId,
          toolName: payload.toolName ?? 'tool',
          call: evt,
          result: null,
          isError: false,
          startedAt: evt.createdAt,
        })
      }
    } else if (
      payload.kind === 'tool-result' ||
      payload.kind === 'tool-error'
    ) {
      if (existing) {
        existing.result = evt
        existing.isError = payload.kind === 'tool-error'
        existing.toolName = payload.toolName ?? existing.toolName
      } else {
        order.push(callId)
        byCallId.set(callId, {
          toolCallId: callId,
          toolName: payload.toolName ?? 'tool',
          call: null,
          result: evt,
          isError: payload.kind === 'tool-error',
          startedAt: evt.createdAt,
        })
      }
    }
  }

  return order.map((id) => byCallId.get(id)!).filter(Boolean)
}

function ToolCallRow({ entry }: { entry: TimelineEntry }) {
  const [expanded, setExpanded] = useState(false)
  const isComplete = !!entry.result
  const isError = entry.isError
  const callPayload = entry.call?.payload
  const resultPayload = entry.result?.payload
  const summary = describeTool(entry.toolName, callPayload, resultPayload)
  const durationMs = readDurationMs(resultPayload)
  const expandable = !!(callPayload?.input || resultPayload?.output)

  return (
    <li className='px-4 py-2 text-[12px]'>
      <button
        type='button'
        onClick={() => expandable && setExpanded((v) => !v)}
        className={`flex w-full items-start gap-2 text-left ${
          expandable ? 'cursor-pointer' : 'cursor-default'
        }`}
        disabled={!expandable}
      >
        <StatusIcon isComplete={isComplete} isError={isError} />
        <div className='min-w-0 flex-1'>
          <div className='flex items-baseline gap-2'>
            <span className='font-medium text-text-secondary'>{entry.toolName}</span>
            {durationMs != null ? (
              <span className='text-[11px] text-text-placeholder'>
                {formatDuration(durationMs)}
              </span>
            ) : null}
          </div>
          {summary ? (
            <div className='mt-0.5 truncate text-[11px] text-muted-foreground'>
              {summary}
            </div>
          ) : null}
        </div>
        {!expandable ? null : expanded ? (
          <ChevronDown className='mt-0.5 h-3 w-3 shrink-0 text-text-placeholder' />
        ) : (
          <ChevronRight className='mt-0.5 h-3 w-3 shrink-0 text-text-placeholder' />
        )}
      </button>
      {expanded ? (
        <div className='mt-2 space-y-2 pl-5'>
          {callPayload?.input ? (
            <PayloadBlock label='Input' value={callPayload.input} />
          ) : null}
          {resultPayload?.output ? (
            <PayloadBlock
              label={isError ? 'Error' : 'Output'}
              value={resultPayload.output}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function LogRow({ entry }: { entry: TimelineEntry }) {
  const message = entry.call?.payload.message ?? ''
  if (!message) return null
  return (
    <li className='flex items-start gap-2 px-4 py-1.5 text-[11px] text-muted-foreground'>
      <MessageSquare className='mt-0.5 h-3 w-3 shrink-0 text-text-disabled' />
      <span className='whitespace-pre-wrap break-words'>{message}</span>
    </li>
  )
}

function StatusIcon({
  isComplete,
  isError,
}: {
  isComplete: boolean
  isError: boolean
}) {
  if (isError) {
    return <CircleAlert className='mt-0.5 h-3.5 w-3.5 shrink-0 text-status-orange' />
  }
  if (isComplete) {
    return (
      <CheckCircle2 className='mt-0.5 h-3.5 w-3.5 shrink-0 text-status-green' />
    )
  }
  return (
    <Loader2 className='mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
  )
}

function PayloadBlock({ label, value }: { label: string; value: unknown }) {
  const formatted = formatPayload(value)
  return (
    <div>
      <div className='text-[10px] font-semibold uppercase tracking-wider text-text-placeholder'>
        {label}
      </div>
      <ScrollArea
        type='always'
        className='mt-1 max-h-[240px] rounded-sm bg-card'
        viewClassName='max-h-[240px] [&>div]:!block'
      >
        <pre className='whitespace-pre-wrap break-words p-2 text-[10px] leading-[16px] text-text-body'>
          {formatted}
        </pre>
      </ScrollArea>
    </div>
  )
}

function describeTool(
  toolName: string,
  call: StepEventPayload | undefined,
  result: StepEventPayload | undefined,
): string | null {
  const input = call?.input as Record<string, unknown> | undefined
  if (input && typeof input.action === 'string' && toolName !== input.action) {
    return String(input.action)
  }
  if (input && typeof input.label === 'string') {
    return input.label
  }
  const output = result?.output as Record<string, unknown> | undefined
  if (output && typeof output.status === 'string') {
    return String(output.status)
  }
  return null
}

function readDurationMs(payload: StepEventPayload | undefined): number | null {
  const meta = payload?.meta
  if (meta && typeof meta.durationMs === 'number') return meta.durationMs
  return null
}

function formatPayload(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}
